/* ---- SCR-003: Event detail page ---- */

import { renderDetailOverlay, renderEventDetailInfo } from '../components/EventDetailPanel';
import { MapViewComponent } from '../components/MapView';
import { issueVc } from '../components/VcIssueButton';
import { ICONS } from '../icons';
import { eventService } from '../services/event.service';
import type { EventDetail, EventItem, Geofence, SpoofingStatus } from '../types';

export class EventDetailPage {
  private el: HTMLElement;
  private mapView: MapViewComponent | null = null;
  private eventId: number;
  private eventBasic: EventItem | null;
  private onBack: () => void;

  constructor(eventId: number, eventBasic: EventItem | null, onBack: () => void) {
    this.el = document.createElement('div');
    this.el.className = 'screen';
    this.eventId = eventId;
    this.eventBasic = eventBasic;
    this.onBack = onBack;
  }

  render(): HTMLElement {
    this.el.innerHTML = '';

    // Header
    const header = document.createElement('div');
    header.className = 'sh';
    header.innerHTML = `<button class="bb" id="detail-back">${ICONS.back}</button><h1>イベント詳細</h1>`;
    this.el.appendChild(header);
    header.querySelector('#detail-back')!.addEventListener('click', this.onBack);

    // Map area
    const mapArea = document.createElement('div');
    mapArea.className = 'ma';
    this.el.appendChild(mapArea);

    const mapTarget = document.createElement('div');
    mapTarget.style.width = '100%';
    mapTarget.style.height = '100%';
    mapArea.appendChild(mapTarget);

    // Map controls
    const controls = document.createElement('div');
    controls.className = 'mc';
    controls.innerHTML = `
      <button class="mb" id="det-zoom-in">${ICONS.zoomIn}</button>
      <button class="mb" id="det-zoom-out">${ICONS.zoomOut}</button>
    `;
    mapArea.appendChild(controls);

    const myLocBtn = document.createElement('button');
    myLocBtn.className = 'mb ml';
    myLocBtn.innerHTML = ICONS.myLocation;
    mapArea.appendChild(myLocBtn);

    // Loading placeholder for info panel
    const infoPlaceholder = document.createElement('div');
    infoPlaceholder.className = 'ip';
    infoPlaceholder.innerHTML = '<div class="loading"><div class="spinner"></div></div>';
    this.el.appendChild(infoPlaceholder);

    // Init map with basic event data if available
    const lat = this.eventBasic?.lat ?? 35.6812;
    const lng = this.eventBasic?.lng ?? 139.7671;

    this.mapView = new MapViewComponent({
      target: mapTarget,
      center: [lng, lat],
      zoom: 16,
    });
    this.mapView.setPin(lng, lat);

    controls.querySelector('#det-zoom-in')!.addEventListener('click', () => this.mapView?.zoomIn());
    controls
      .querySelector('#det-zoom-out')!
      .addEventListener('click', () => this.mapView?.zoomOut());
    myLocBtn.addEventListener('click', () => this.mapView?.centerOn(lng, lat));

    // Load geofences for the map
    this.loadGeofences();

    // Load full event detail
    this.loadDetail(mapArea, infoPlaceholder);

    return this.el;
  }

  private async loadDetail(mapArea: HTMLElement, infoPlaceholder: HTMLElement): Promise<void> {
    let detail: EventDetail;
    try {
      detail = await eventService.getEvent(this.eventId);
      console.log('[EventDetailPage] Loaded event detail id=' + this.eventId, {
        start_hash: detail.start_hash,
        end_hash: detail.end_hash,
        verified: detail.verified,
      });
    } catch (err) {
      // Server 取得失敗時。基本情報から hash 空のフォールバックを構築するが、
      // 旧コードのような偽ハッシュ ('abc123def456' 等) を作らないよう空文字を使う。
      // EventDetailPanel.shortenHash は空文字を「—」と表示する。
      console.warn('[EventDetailPage] Failed to fetch event detail id=' + this.eventId, err);
      if (!this.eventBasic) {
        infoPlaceholder.innerHTML = '<div class="em">イベントが見つかりません</div>';
        return;
      }
      const mockSpoofing: SpoofingStatus = {
        doppler: 'ok',
        imu: 'ok',
        signal_quality: 'ok',
        time: 'ok',
        pressure_altitude: 'ok',
        doppler_raim: 'ok',
        mock_location: 'ok',
      };
      detail = {
        ...this.eventBasic,
        start_hash: '',
        end_hash: '',
        vc_id: null,
        vc_issued_at: null,
        spoofing_status: mockSpoofing,
      };
    }

    // Add overlay
    const overlay = renderDetailOverlay(detail);
    mapArea.appendChild(overlay);

    // Center map on event
    this.mapView?.centerOn(detail.lng, detail.lat);
    this.mapView?.setPin(detail.lng, detail.lat);

    // Replace info panel
    const infoPanel = renderEventDetailInfo(detail, () => issueVc(detail));
    infoPlaceholder.replaceWith(infoPanel);
  }

  private async loadGeofences(): Promise<void> {
    try {
      const res = await eventService.listGeofences();
      this.mapView?.setGeofences(res.geofences);
    } catch {
      // If the event has fence_id and basic location, show a simple geofence area
      if (this.eventBasic) {
        const mockGf: Geofence = {
          id: this.eventBasic.fence_id,
          name: this.eventBasic.fence,
          description: null,
          category: 'monitoring',
          color: '#0066FF',
          coordinates: [
            [this.eventBasic.lng - 0.005, this.eventBasic.lat + 0.003],
            [this.eventBasic.lng + 0.005, this.eventBasic.lat + 0.003],
            [this.eventBasic.lng + 0.005, this.eventBasic.lat - 0.003],
            [this.eventBasic.lng - 0.005, this.eventBasic.lat - 0.003],
            [this.eventBasic.lng - 0.005, this.eventBasic.lat + 0.003],
          ],
        };
        this.mapView?.setGeofences([mockGf]);
      }
    }
  }

  destroy(): void {
    this.mapView?.destroy();
    this.mapView = null;
  }
}
